<!-- 
  @component A blurred thumbnail version of a CDN image, using minimal KB  
  Good for using on backgrounds, or placeholders for lazy loading images.

  **⚠️ on local-dev, it will not use CDN-blurred images but will use
  the original image faked with a blur filter. So always check in staging**
-->
<script lang="ts">
  import { dev } from '$app/environment';
  import { onMount, createEventDispatcher } from 'svelte';
  import { getBlurImage } from './image';

  export let el: HTMLImageElement | null = null;
  /** the image source, if dev environment, srcset wont generate for staging assets */
  export let src: string | undefined | null = null;
  export let lazy: boolean = true;
  export let decode: boolean = true;
  export let style: string | null = null;

  /**
   * the `width` of the image, used to generate the blur image
   *
   * **for larger blurred images, like covers/headers, a larger `width` like `128` is recommended**
   *
   * but for most use cases, a smaller width like `32` is perfect for `kb` reduction.
   */
  export let width: number = 32;
  export let blur: number = Math.min(width / 4, 10);
  export let quality: number = 70;

  /** instruction for image resizer on server. can be used to reduce kb on cropped images
   * https://developers.cloudflare.com/images/cloudflare-images/transform/flexible-variants/#fit */
  export let fit: 'scale-down' | 'cover' | 'contain' | 'crop' = 'cover';
  /** instruction for image resizer on server. where to anchor the cropping
   * https://developers.cloudflare.com/images/cloudflare-images/transform/flexible-variants/#gravity */
  export let gravity: string | undefined = undefined;

  let className: string | null = null;
  export { className as class };
  export let animClass: string | null = 'animate-fade-in-slow';

  let loaded = false;
  const dispatch = createEventDispatcher();

  $: source = src ? getBlurImage(src, width, blur, fit, gravity, quality) : '';

  onMount(() => {
    if (el?.complete) {
      loaded = true;
      dispatch('load');
    }
  });
</script>

<img
  bind:this={el}
  loading={lazy ? 'lazy' : null}
  decoding={decode ? 'async' : null}
  src={source || ''}
  class="{className} {loaded ? animClass : '!opacity-0'} text-[0] blur-[var(--blur-value)]"
  style="{style ? style : ''} --blur-value:{blur}px;"
  alt=""
  draggable={false}
  on:load={() => (loaded = true)}
  on:load
  on:error
/>
